import { Button, Grid, Typography, useTheme } from '@mui/material';
import { first, isNumber } from 'es-toolkit/compat';
import React, { FunctionComponent, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { CaseSearchItem } from 'services/searchIndex';
import { BooleanParam, useQueryParam } from 'use-query-params';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useNavigationToViewerPage } from 'utils/useNavigationToViewerPage';
import { unknownStudyId, unknownStudyName } from './FullSearch';
import SearchResultMatch from './SearchResultMatch';

interface SearchResultProps {
  option: CaseSearchItem;
  notInCurrentStudy?: boolean;
  searchQuery?: string;
  debug?: boolean;
}

const SearchResult: FunctionComponent<React.PropsWithChildren<SearchResultProps>> = ({
  option,
  notInCurrentStudy,
  searchQuery,
  debug = false,
}) => {
  const theme = useTheme();
  const { getUrlToCasePage, getUrlToSlidePage } = useNavigationToViewerPage();
  const [slidesMode] = useQueryParam('slidesMode', BooleanParam);
  const { labId } = useCurrentLabId();

  const slideInfo = first(option.slides);

  const urlToPage = useMemo(() => {
    if (slidesMode || option.isSlideOnly) {
      const slideId = slideInfo?.id;
      if (!slideId) {
        console.error(`trying to move to slides page without slide id, option: `, option);
        return '';
      }

      const studyIdFromResult = option?.studyId || slideInfo?.studyId;
      const resultCaseId = isNumber(option.caseId)
        ? option.caseId
        : isNumber(slideInfo.caseId)
        ? slideInfo.caseId
        : undefined;
      const shouldClearCurrentStudyFilter = Boolean(slideInfo?.labId && slideInfo.labId !== labId && !resultCaseId);

      return getUrlToSlidePage({
        slideId: slideId,
        labId: slideInfo.labId,
        // Pass caseId and caseStudyId if they are available
        caseId: resultCaseId,
        caseStudyId: studyIdFromResult !== unknownStudyId ? studyIdFromResult : undefined,
        ...(shouldClearCurrentStudyFilter ? { clearCurrentStudyFilter: true } : {}),
      });
    } else {
      const caseId = option.caseId as number;
      if (!caseId) {
        console.error(`trying to move to case page without case id, option: `, option);
        return '';
      }

      return getUrlToCasePage({
        caseId: caseId,
        caseStudyId: option.studyId,
      });
    }
  }, [slidesMode, slideInfo, option, getUrlToSlidePage, getUrlToCasePage, labId]);

  return (
    <Grid container justifyContent="space-between" alignItems="center" flexWrap={'nowrap'}>
      <Grid item container xs flexGrow={1} overflow="hidden" whiteSpace="nowrap">
        <Grid container item xs={12} direction="row">
          <Grid item xs={12} container justifyContent="flex-start" alignItems="center" spacing={1}>
            <Grid item sx={{ minWidth: 0, overflow: 'hidden' }}>
              <Typography
                variant="h4"
                color="text.primary"
                noWrap
                title={slidesMode ? slideInfo?.id : option.caseLabel}
              >
                {slidesMode ? slideInfo?.id : option.caseLabel}
              </Typography>
            </Grid>
            <Grid item>
              {notInCurrentStudy && option.studyName != unknownStudyName && (
                <Typography variant="body1" color="text.secondary">
                  ({option.studyName})
                </Typography>
              )}
              {option.isSlideOnly && (
                <Typography variant="body1" color="text.secondary">
                  (
                  {slideInfo?.labId === labId
                    ? 'Lab lost and found slide'
                    : `Lab: ${slideInfo?.labName}${
                        slideInfo?.caseId ? ` | Case: ${slideInfo?.caseLabel || slideInfo.caseId}` : ''
                      }`}
                  )
                </Typography>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            minWidth: 0,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          <SearchResultMatch option={option} searchQuery={searchQuery} />
        </Grid>
      </Grid>
      <Grid item>
        <Button
          variant="contained"
          size="small"
          disableElevation
          component={Link}
          sx={{
            textTransform: 'none',
            backgroundColor:
              theme.palette.mode == 'light' ? theme.palette.secondary.light : theme.palette.secondary.dark,
            '&:hover': {
              backgroundColor: theme.palette.secondary.main,
            },
            color: theme.palette.mode == 'light' ? theme.palette.secondary.dark : theme.palette.secondary.light,
          }}
          to={urlToPage}
          disabled={urlToPage === ''}
          onClick={(event) => {
            if (debug) {
              console.debug(`Navigating to ${slidesMode ? 'slide' : 'case'} page: `, urlToPage);
            }
            event.stopPropagation();
          }}
        >
          Go to {option.isSlideOnly || slidesMode ? 'slide' : 'case'}
        </Button>
      </Grid>
    </Grid>
  );
};

export default SearchResult;
